import { api } from "@/lib/api";

function withParams(path, params = {}) {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== "") search.set(k, String(v));
  });
  const qs = search.toString();
  return qs ? `${path}?${qs}` : path;
}

function unwrap(data) {
  if (data && typeof data === "object" && "data" in data) return data.data;
  return data;
}

function toArray(data) {
  const payload = unwrap(data);
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.results)) return payload.results;
  return [];
}

function normalizeRecipeRow(row) {
  if (!row || typeof row !== "object") return row;
  const ingredients = Array.isArray(row.ingredients)
    ? row.ingredients.map((ing) => ({
        ...ing,
        material_id: ing.material_id ?? ing.material?.id ?? null,
      }))
    : [];
  const id = row.id ?? row.version_id ?? row.pk ?? null;
  const grade_id = row.grade_id ?? row.grade?.id ?? null;
  return {
    ...row,
    ...(id != null ? { id } : {}),
    ...(grade_id != null ? { grade_id } : {}),
    ingredients,
  };
}

async function getOne(url, params) {
  const res = await api.get(url, params ? { params } : undefined);
  return { data: unwrap(res.data) };
}

async function getList(url, params) {
  const res = await api.get(url, params ? { params } : undefined);
  return { data: toArray(res.data) };
}

async function getPaginatedList(url, params) {
  const res = await api.get(url, params ? { params } : undefined);
  const raw = res.data;
  const envelopeMeta =
    raw &&
    typeof raw === "object" &&
    !Array.isArray(raw) &&
    raw.meta != null &&
    typeof raw.meta === "object"
      ? raw.meta
      : null;

  const inner = unwrap(raw);

  if (Array.isArray(inner)) {
    if (envelopeMeta) {
      return { data: { results: inner, meta: envelopeMeta } };
    }
    return { data: inner };
  }

  if (inner && typeof inner === "object" && !Array.isArray(inner)) {
    if (envelopeMeta != null && inner.meta == null) {
      return { data: { ...inner, meta: envelopeMeta } };
    }
    return { data: inner };
  }

  return { data: inner };
}

async function postOne(url, body) {
  const res = await api.post(url, body);
  return { data: unwrap(res.data) };
}

async function putOne(url, body) {
  const res = await api.put(url, body);
  return { data: unwrap(res.data) };
}

async function patchOne(url, body) {
  const res = await api.patch(url, body);
  return { data: unwrap(res.data) };
}

async function deleteOne(url) {
  const res = await api.delete(url);
  return { data: unwrap(res.data) };
}

async function postForm(url, formData) {
  const res = await api.post(url, formData);
  return { data: unwrap(res.data) };
}

async function putForm(url, formData) {
  const res = await api.put(url, formData);
  return { data: unwrap(res.data) };
}

const DRIVER_FILE_FIELDS = [
  "doc_pan",
  "doc_aadhar_front",
  "doc_aadhar_back",
  "doc_licence_front",
  "doc_licence_back",
  "doc_pf_certificate",
];

function normalizeDriverBankAccounts(rows) {
  if (!Array.isArray(rows)) return [];
  return rows
    .map((r) => ({
      account_no: String(r?.account_no ?? "").trim(),
      ifsc_code: String(r?.ifsc_code ?? "").trim(),
      bank_name: String(r?.bank_name ?? "").trim(),
      branch: String(r?.branch ?? "").trim(),
      account_type: String(r?.account_type ?? "SAVINGS").trim() || "SAVINGS",
    }))
    .filter((r) => r.account_no || r.ifsc_code || r.bank_name || r.branch);
}

function driverPayloadHasMultipartFiles(row) {
  return DRIVER_FILE_FIELDS.some((k) => row[k] instanceof File && row[k]?.size > 0);
}

const VEHICLE_DOC_FILE_FIELDS = [
  "doc_rc_book",
  "doc_tax",
  "doc_permit",
  "doc_loan_schedule",
  "doc_puc",
  "doc_noc",
];

function vehiclePayloadHasMultipartFiles(row) {
  return VEHICLE_DOC_FILE_FIELDS.some((k) => row[k] instanceof File && row[k]?.size > 0);
}

function toDecimalString(v) {
  const n = Number(String(v ?? "").replace(/,/g, ""));
  if (!Number.isFinite(n)) return String(v ?? "").trim();
  return n.toFixed(2);
}

function buildVehicleMultipartData(row) {
  const fd = new FormData();
  fd.append("registration_no", String(row.registration_no ?? ""));
  const cap = String(row.capacity_cum ?? "").trim();
  if (cap !== "") {
    fd.append("capacity_cum", toDecimalString(cap));
  }
  for (const key of VEHICLE_DOC_FILE_FIELDS) {
    const f = row[key];
    if (f instanceof File && f.size > 0) {
      fd.append(key, f);
    }
  }
  return fd;
}

function buildDriverMultipartData(row) {
  const fd = new FormData();
  fd.append("name", row.name ?? "");
  fd.append("employee_id", row.employee_id ?? "");
  fd.append("address", row.address ?? "");
  fd.append("contact_no", row.contact_no ?? row.contact ?? "");
  fd.append("is_active", row.is_active !== false ? "true" : "false");
  fd.append("bank_accounts", JSON.stringify(normalizeDriverBankAccounts(row.bank_accounts)));

  for (const key of DRIVER_FILE_FIELDS) {
    const f = row[key];
    if (f instanceof File && f.size > 0) {
      fd.append(key, f);
    }
  }

  const removeDocs = Array.isArray(row.remove_documents)
    ? row.remove_documents.filter(Boolean)
    : [];
  if (removeDocs.length > 0) {
    fd.append("remove_documents", JSON.stringify(removeDocs));
  }

  return fd;
}

export const rmcClient = {
  async getCompany() {
    return getOne("/api/v1/company/");
  },

  async putCompany(payload) {
    return putOne("/api/v1/company/", payload);
  },

  async listFinancialYears() {
    return getList("/api/v1/financial-years/");
  },

  async createFinancialYear(payload) {
    return postOne("/api/v1/financial-years/", payload);
  },

  async activateFinancialYear(id) {
    const url = `/api/v1/financial-years/${id}/activate/`;
    try {
      return await postOne(url);
    } catch (err) {
      if (err?.response?.status !== 405) throw err;
      try {
        const putRes = await api.put(url);
        return { data: unwrap(putRes.data) };
      } catch (putErr) {
        if (putErr?.response?.status !== 405) throw putErr;
        const patchRes = await api.patch(url);
        return { data: unwrap(patchRes.data) };
      }
    }
  },

  async updateFinancialYear(id, payload) {
    return putOne(`/api/v1/financial-years/${id}/`, payload);
  },

  async deleteFinancialYear(id) {
    return deleteOne(`/api/v1/financial-years/${id}/`);
  },

  async getActiveFY() {
    const rows = (await getList("/api/v1/financial-years/")).data;
    return { data: rows.find((fy) => fy.is_active) || null };
  },

  async listMaterials(params = {}) {
    return getList("/api/v1/purchase/materials/", params);
  },

  async createMaterial(row) {
    return postOne("/api/v1/purchase/materials/", row);
  },

  async updateMaterial(id, row) {
    return putOne(`/api/v1/purchase/materials/${id}/`, row);
  },

  async patchMaterial(id, row) {
    return patchOne(`/api/v1/purchase/materials/${id}/`, row);
  },

  async deleteMaterial(id) {
    return deleteOne(`/api/v1/purchase/materials/${id}/`);
  },

  async listPurchaseParties(params = {}) {
    return getList("/api/v1/purchase/parties/", params);
  },

  async createPurchaseParty(row) {
    return postOne("/api/v1/purchase/parties/", row);
  },

  async getPurchaseParty(id, params = {}) {
    return getOne(`/api/v1/purchase/parties/${id}/`, params);
  },

  async updatePurchaseParty(id, payload) {
    return putOne(`/api/v1/purchase/parties/${id}/`, payload);
  },

  async deletePurchaseParty(id) {
    return deleteOne(`/api/v1/purchase/parties/${id}/`);
  },

  async listSalesParties() {
    return getList("/api/v1/sales/parties/");
  },

  async createSalesParty(row) {
    return postOne("/api/v1/sales/parties/", row);
  },

  async updateSalesParty(id, row) {
    return putOne(`/api/v1/sales/parties/${id}/`, row);
  },

  async deleteSalesParty(id) {
    return deleteOne(`/api/v1/sales/parties/${id}/`);
  },

  async addSalesSite(partyId, payloadOrName) {
    const body =
      typeof payloadOrName === "string"
        ? { name: payloadOrName }
        : { ...payloadOrName };
    return postOne(`/api/v1/sales/parties/${partyId}/sites/`, body);
  },

  async updateSalesSite(partyId, siteId, row) {
    return putOne(`/api/v1/sales/parties/${partyId}/sites/${siteId}/`, row);
  },

  async deleteSalesSite(partyId, siteId) {
    return deleteOne(`/api/v1/sales/parties/${partyId}/sites/${siteId}/`);
  },

  async listGrades(params = {}) {
    return getList("/api/v1/items/grades/", params);
  },

  async createGrade(payload) {
    const body = typeof payload === "string" ? { name: payload } : payload;
    return postOne("/api/v1/items/grades/", {
      party_id: body.party_id,
      name: body.name,
      ...(body.uom != null && body.uom !== "" ? { uom: body.uom } : {}),
    });
  },

  async updateGrade(id, payload) {
    return putOne(`/api/v1/items/grades/${id}/`, payload);
  },

  async listRecipesForGrade(gradeId, partyId) {
    const res = await getOne("/api/v1/items/recipes/", {
      grade_id: gradeId,
      ...(partyId != null ? { party_id: partyId } : {}),
    });
    const payload = res.data;
    if (!payload) return { data: [] };
    if (Array.isArray(payload)) {
      return { data: payload.map(normalizeRecipeRow) };
    }
    if (Array.isArray(payload?.results)) {
      return { data: payload.results.map(normalizeRecipeRow) };
    }
    return { data: [normalizeRecipeRow(payload)] };
  },

  async addRecipeVersion(payload) {
    return postOne("/api/v1/items/recipes/", payload);
  },

  async updateRecipeVersion(recipeId, payload) {
    return putOne(`/api/v1/items/recipes/by-id/${recipeId}/`, payload);
  },

  async deleteRecipeVersion(gradeId, partyId) {
    return deleteOne(withParams(`/api/v1/items/recipes/${gradeId}/`, { party_id: partyId }));
  },

  async listDrivers(params = {}) {
    return getList(withParams("/api/v1/transport/drivers/", params));
  },

  async getDriver(id) {
    return getOne(`/api/v1/transport/drivers/${id}/`);
  },

  async createDriver(row) {
    if (driverPayloadHasMultipartFiles(row)) {
      return postForm("/api/v1/transport/drivers/", buildDriverMultipartData(row));
    }
    return postOne("/api/v1/transport/drivers/", {
      name: row.name ?? "",
      employee_id: row.employee_id ?? "",
      address: row.address ?? "",
      contact_no: row.contact_no ?? row.contact ?? "",
      is_active: row.is_active !== false,
      bank_accounts: normalizeDriverBankAccounts(row.bank_accounts),
    });
  },

  async updateDriver(id, row) {
    const removeDocs = Array.isArray(row.remove_documents)
      ? row.remove_documents.filter(Boolean)
      : [];
    const useMultipart =
      driverPayloadHasMultipartFiles(row) || removeDocs.length > 0;

    if (useMultipart) {
      return putForm(`/api/v1/transport/drivers/${id}/`, buildDriverMultipartData(row));
    }
    const jsonPayload = {
      name: row.name ?? "",
      employee_id: row.employee_id ?? "",
      address: row.address ?? "",
      contact_no: row.contact_no ?? row.contact ?? "",
      is_active: row.is_active !== false,
      bank_accounts: normalizeDriverBankAccounts(row.bank_accounts),
    };
    if (typeof row.remove_kyc_doc === "boolean") {
      jsonPayload.remove_kyc_doc = row.remove_kyc_doc;
    }
    return putOne(`/api/v1/transport/drivers/${id}/`, jsonPayload);
  },

  async deleteDriver(id) {
    return deleteOne(`/api/v1/transport/drivers/${id}/`);
  },

  async listVehicles() {
    return getList("/api/v1/transport/vehicles/");
  },

  async getVehicle(id) {
    return getOne(`/api/v1/transport/vehicles/${id}/`);
  },

  async getVehicleDriver(vehicleId) {
    return getOne(`/api/v1/transport/vehicles/${vehicleId}/driver/`);
  },

  async createVehicle(row) {
    return postOne("/api/v1/transport/vehicles/", row);
  },

  async updateVehicle(id, row) {
    const useMultipart =
      vehiclePayloadHasMultipartFiles(row) ||
      row?._vehicleMultipart === true ||
      row?.__vehicleMultipart === true;
    if (useMultipart) {
      const { _vehicleMultipart, __vehicleMultipart, ...rest } = row;
      return putForm(`/api/v1/transport/vehicles/${id}/`, buildVehicleMultipartData(rest));
    }
    return putOne(`/api/v1/transport/vehicles/${id}/`, row);
  },

  async deleteVehicle(id) {
    return deleteOne(`/api/v1/transport/vehicles/${id}/`);
  },

  async getFuelReadingContext(vehicleId) {
    return getOne(`/api/v1/transport/fuel-reading-context/`, {
      vehicle_id: vehicleId,
    });
  },

  async listFuelReadings(params = {}) {
    return getPaginatedList("/api/v1/transport/fuel-readings/", params);
  },

  async getFuelReadingNumber() {
    return getOne("/api/v1/transport/fuel-readings/number/");
  },

  async getFuelReading(id) {
    return getOne(`/api/v1/transport/fuel-readings/${id}/`);
  },

  async createFuelReading(row) {
    return postOne("/api/v1/transport/fuel-readings/", row);
  },

  async updateFuelReading(id, row) {
    return putOne(`/api/v1/transport/fuel-readings/${id}/`, row);
  },

  async deleteFuelReading(id) {
    return deleteOne(`/api/v1/transport/fuel-readings/${id}/`);
  },

  /** Vehicle EMI / finance records (loan, EMI, insurance, permits). */
  async listVehicleFinanceRecords(params = {}) {
    return getPaginatedList("/api/v1/transport/vehicle-finance-records/", params);
  },

  async getVehicleFinanceRecord(id) {
    return getOne(`/api/v1/transport/vehicle-finance-records/${id}/`);
  },

  async createVehicleFinanceRecord(row) {
    return postOne("/api/v1/transport/vehicle-finance-records/", row);
  },

  async updateVehicleFinanceRecord(id, row) {
    return putOne(`/api/v1/transport/vehicle-finance-records/${id}/`, row);
  },

  async deleteVehicleFinanceRecord(id) {
    return deleteOne(`/api/v1/transport/vehicle-finance-records/${id}/`);
  },

  async listGateInward(filters = {}) {
    return getList("/api/v1/gate/inward/", filters);
  },

  async createGateInward(row) {
    return postOne("/api/v1/gate/inward/", row);
  },

  async listGateOutward() {
    return getList("/api/v1/gate/outward/");
  },

  async createGateOutward(row) {
    return postOne("/api/v1/gate/outward/", row);
  },

  async listPartyMaterialQuarries(params = {}) {
    return getList("/api/v1/purchase/party-material-quarries/", params);
  },

  async createPartyMaterialQuarry(row) {
    return postOne("/api/v1/purchase/party-material-quarries/", row);
  },

  async updatePartyMaterialQuarry(id, row) {
    return putOne(`/api/v1/purchase/party-material-quarries/${id}/`, row);
  },

  async listConcreteQuotations(params = {}) {
    return getPaginatedList("/api/v1/purchase/concrete-quotations/", params);
  },

  async getConcreteQuotation(id) {
    return getOne(`/api/v1/purchase/concrete-quotations/${id}/`);
  },

  async createConcreteQuotation(row) {
    return postOne("/api/v1/purchase/concrete-quotations/", row);
  },

  async updateConcreteQuotation(id, row) {
    return putOne(`/api/v1/purchase/concrete-quotations/${id}/`, row);
  },

  /** GET quarry-wise rate for party + material + quarry (active concrete quotation). */
  async getConcreteQuotationRate(params = {}) {
    return getOne("/api/v1/purchase/concrete-quotation-rate/", params);
  },

  async listPurchaseChallans(params = {}) {
    return getPaginatedList("/api/v1/purchase/challans/", params);
  },

  async getPurchaseChallanNumber() {
    return getOne("/api/v1/purchase/challans/number/");
  },

  async createPurchaseChallan(row) {
    return postOne("/api/v1/purchase/challans/", row);
  },

  async getPurchaseChallan(id) {
    return getOne(`/api/v1/purchase/challans/${id}/`);
  },

  async updatePurchaseChallan(id, row) {
    return putOne(`/api/v1/purchase/challans/${id}/`, row);
  },

  async deletePurchaseChallan(id) {
    return deleteOne(`/api/v1/purchase/challans/${id}/`);
  },

  /** Multipart upload: `file` must be .xlsx / .xls (concrete challan bulk import). */
  async importConcretePurchaseChallans(file) {
    const fd = new FormData();
    fd.append("file", file);
    return postForm("/api/v1/purchase/challans/import-concrete/", fd);
  },

  async listPurchaseInvoices(params = {}) {
    return getPaginatedList("/api/v1/purchase/invoices/", params);
  },

  async getPurchaseInvoice(id) {
    return getOne(`/api/v1/purchase/invoices/${id}/`);
  },

  async createPurchaseInvoice(payload) {
    return postOne("/api/v1/purchase/invoices/", payload);
  },

  async deletePurchaseInvoice(id) {
    return deleteOne(`/api/v1/purchase/invoices/${id}/`);
  },

  async listQuotations(params = {}) {
    return getList("/api/v1/sales/quotations/", params);
  },

  async createQuotation(row) {
    return postOne("/api/v1/sales/quotations/", row);
  },

  async getQuotation(id) {
    return getOne(`/api/v1/sales/quotations/${id}/`);
  },

  async updateQuotation(id, row) {
    return putOne(`/api/v1/sales/quotations/${id}/`, row);
  },

  async setQuotationActive(id, is_active) {
    return putOne(`/api/v1/sales/quotations/${id}/`, { is_active: Boolean(is_active) });
  },

  async lookupQuotationRate(params) {
    return getOne("/api/v1/sales/quotations/rate/", params);
  },

  async listSalesChallans(params = {}) {
    return getPaginatedList("/api/v1/sales/challans/", params);
  },

  async getSalesChallanNumber() {
    return getOne("/api/v1/sales/challans/number/");
  },

  async createSalesChallan(row) {
    return postOne("/api/v1/sales/challans/", row);
  },

  async createSalesChallansBulk(payload) {
    return postOne("/api/v1/sales/challans/bulk/", payload);
  },

  async getSalesChallan(id) {
    return getOne(`/api/v1/sales/challans/${id}/`);
  },

  async updateSalesChallan(id, row) {
    return putOne(`/api/v1/sales/challans/${id}/`, row);
  },

  async deleteSalesChallan(id) {
    return deleteOne(`/api/v1/sales/challans/${id}/`);
  },

  async listSalesInvoices(params = {}) {
    return getPaginatedList("/api/v1/sales/invoices/", params);
  },

  async getSalesInvoice(id) {
    return getOne(`/api/v1/sales/invoices/${id}/`);
  },

  async createSalesInvoice(payload) {
    return postOne("/api/v1/sales/invoices/", payload);
  },

  async createSalesInvoicesBulk(payload) {
    return postOne("/api/v1/sales/invoices/bulk/", payload);
  },

  async updateSalesInvoice(id, row) {
    return patchOne(`/api/v1/sales/invoices/${id}/`, row);
  },

  async deleteSalesInvoice(id) {
    return deleteOne(`/api/v1/sales/invoices/${id}/`);
  },

  async getStockPeriodReport(params) {
    return getOne("/api/v1/stock/report/", params);
  },

  async getConsumptionReport(params) {
    return getPaginatedList("/api/v1/stock/consumption/", params);
  },

  async getMaterialConsumptionReport(params) {
    return getPaginatedList("/api/v1/stock/consumption/material/", params);
  },

  async getStockOverview() {
    return getOne("/api/v1/stock/current/");
  },

  async getStockDashboard() {
    return getOne("/api/v1/stock/dashboard/");
  },

  async getStockLedger(materialId) {
    return getOne(`/api/v1/stock/ledger/${materialId}/`);
  },

  async getLedger(partyId, params = {}) {
    return getOne(`/api/v1/accounts/ledger/${partyId}/`, params);
  },

  async getPurchaseLedger(partyId, params = {}) {
    return getOne(`/api/v1/accounts/purchase-ledger/${partyId}/`, params);
  },

  async getTrialBalance(params = {}) {
    return getOne("/api/v1/accounts/trial-balance/", params);
  },

  async getPartyBalance(party_id) {
    return getOne(`/api/v1/accounts/balance/${party_id}/`);
  },

  async getPurchasePartyBalance(party_id) {
    return getOne(`/api/v1/accounts/purchase-balance/${party_id}/`);
  },

  async listReceipts(params = {}) {
    return getPaginatedList("/api/v1/accounts/receipts/", params);
  },

  async getReceiptNumber() {
    return getOne("/api/v1/accounts/receipts/number/");
  },

  async createReceipt(row) {
    return postOne("/api/v1/accounts/receipts/", row);
  },

  async deleteReceipt(id) {
    return deleteOne(`/api/v1/accounts/receipts/${id}/`);
  },

  async listMiscExpenses(params = {}) {
    return getPaginatedList("/api/v1/purchase/misc-expenses/", params);
  },

  async getMiscExpense(id) {
    return getOne(`/api/v1/purchase/misc-expenses/${id}/`);
  },

  async createMiscExpense(payload) {
    return postOne("/api/v1/purchase/misc-expenses/", payload);
  },

  async updateMiscExpense(id, payload) {
    return putOne(`/api/v1/purchase/misc-expenses/${id}/`, payload);
  },

  async deleteMiscExpense(id) {
    return deleteOne(`/api/v1/purchase/misc-expenses/${id}/`);
  },

  async listPurchasePayments(params = {}) {
    return getPaginatedList("/api/v1/accounts/purchase-payments/", params);
  },

  async createPurchasePayment(payload) {
    return postOne("/api/v1/accounts/purchase-payments/", payload);
  },

  async deletePurchasePayment(id) {
    return deleteOne(`/api/v1/accounts/purchase-payments/${id}/`);
  },

  async getSalesReport(params = {}) {
    return getPaginatedList("/api/v1/accounts/report/sales/", params);
  },

  async getSalesPartySummaryReport(params = {}) {
    return getPaginatedList("/api/v1/accounts/report/sales/party-summary/", params);
  },

  async listGstMasters(params = {}) {
    return getList("/api/v1/items/gst-masters/", params);
  },

  async createGstMaster(payload) {
    return postOne("/api/v1/items/gst-masters/", payload);
  },

  async getGstMaster(id, params = {}) {
    return getOne(`/api/v1/items/gst-masters/${id}/`, params);
  },

  async updateGstMaster(id, payload) {
    return putOne(`/api/v1/items/gst-masters/${id}/`, payload);
  },

  async deleteGstMaster(id) {
    return deleteOne(`/api/v1/items/gst-masters/${id}/`);
  },
};
